import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ClusterService from "../../services/api/cluster";
import SearchSelect from "../../components/common/SearchSelect";
import { API_RESPONSE, hasHTMLTags } from "../../settings/shared/constants";
import { convertTextToTitleCase, provisionerTypeLogo } from "../../settings/shared/commonMethods";
import { cluster as clusterMessages } from "../../settings/shared/CommonMessages";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";

const T = {
  themePrimary: "var(--activemenu, #3d91e4)",
  border: "rgba(0,0,0,0.07)",
  borderMid: "rgba(0,0,0,0.12)",
  bg: "#f5f6fa",
  surface: "#ffffff",
  textPrimary: "#0f172a",
  textSecondary: "#64748b",
  textMuted: "#94a3b8",
  fontSans: "'DM Sans','Segoe UI',sans-serif",
  fontMono: "'DM Mono','Courier New',monospace",
};

const EMPTY_FORM = { domain: "", certs: "" };
const CLUSTER_PAGE_SIZE = 100;

const STATUS_STYLE = {
  RUNNING: { bg: "#eaf3de", color: "#3b6d11", label: "Running" },
  READY: { bg: "#eaf3de", color: "#3b6d11", label: "Ready" },
  PENDING: { bg: "#fef3c7", color: "#92400e", label: "Pending" },
  FAILED: { bg: "#fee2e2", color: "#b91c1c", label: "Failed" },
  UNKNOWN: { bg: "#f1f5f9", color: "#64748b", label: "Unknown" },
};

function getClusterStatusStyle(status) {
  const s = String(status || "").toUpperCase();
  if (s.includes("RUN") || s.includes("ACTIVE")) return STATUS_STYLE.RUNNING;
  if (s.includes("READY")) return STATUS_STYLE.READY;
  if (s.includes("PEND") || s.includes("PROVISION") || s.includes("CREAT")) return STATUS_STYLE.PENDING;
  if (s.includes("FAIL") || s.includes("ERROR") || s.includes("STOP")) return STATUS_STYLE.FAILED;
  return STATUS_STYLE.UNKNOWN;
}

function getClusterId(item) {
  return item?.id || item?.clusterId || "";
}

function isValidDomain(value) {
  const domain = String(value || "").trim();
  if (!domain) return false;
  return /^(\*\.)?([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[A-Za-z]{2,}$/.test(domain);
}

function isValidCertArn(value) {
  const cert = String(value || "").trim();
  if (!cert) return false;
  return /^arn:[^\s]+$/i.test(cert);
}

function extractConfig(res, clusterId) {
  const candidates = [
    res?.dto,
    res?.k8sClusterConfigDTO,
    res?.clusterConfigDTO,
    res?.k8sWorkloadDTO,
    res?.clusterWorkloadDTO,
    res?.workloadDTO,
    res?.data,
    res,
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    if (
      candidate.clusterId != null
      || candidate.domain != null
      || candidate.certs != null
      || candidate.certificate != null
      || candidate.certArn != null
    ) {
      return {
        clusterId: String(candidate.clusterId || clusterId || ""),
        domain: candidate.domain || "",
        certs: candidate.certs || candidate.certificate || candidate.certArn || "",
      };
    }
  }
  return null;
}

function Field({ label, required, error, hint, children }) {
  return (
    <div className="cc-field">
      {label ? (
        <label className="cc-label">
          {label}
          {required ? <span className="cc-required">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? <div className="cc-error">{error}</div> : hint ? <div className="cc-hint">{hint}</div> : null}
    </div>
  );
}

function Input({ error, mono, ...rest }) {
  return (
    <input
      {...rest}
      className={`cc-input${error ? " is-invalid" : ""}${mono ? " is-mono" : ""}`}
    />
  );
}

function IcGlobe() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
      <ellipse cx="8" cy="8" rx="2.6" ry="6.5" stroke="currentColor" strokeWidth="1.2" />
      <line x1="1.5" y1="8" x2="14.5" y2="8" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function IcLock() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="7" width="10" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5.2 7V5.2a2.8 2.8 0 0 1 5.6 0V7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function IcCopy() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M10.5 5.5V3.8A1.3 1.3 0 0 0 9.2 2.5H3.8A1.3 1.3 0 0 0 2.5 3.8v5.4A1.3 1.3 0 0 0 3.8 10.5H5.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function IcCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <polyline points="3,8.5 6.5,12 13,4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IcPlus() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IcArrowLeft() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <polyline points="9,2 4,7 9,12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CREATE_STEPS = [
  { label: "Cluster", hint: "Choose a cluster" },
  { label: "Domain & TLS", hint: "Enter domain and certificate" },
];

function CreateStepper({ current }) {
  return (
    <div className="cc-stepper">
      {CREATE_STEPS.map((step, i) => {
        const num = i + 1;
        const done = num < current;
        const active = num === current;
        return (
          <div key={step.label} className={`cc-step${active ? " is-active" : ""}${done ? " is-done" : ""}`}>
            <div className="cc-step-rail">
              <div className="cc-step-num">{done ? <IcCheck /> : num}</div>
              {i < CREATE_STEPS.length - 1 ? <div className="cc-step-line" /> : null}
            </div>
            <div className="cc-step-copy">
              <div className="cc-step-label">{step.label}</div>
              <div className="cc-step-hint">{step.hint}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button type="button" className="cc-copy" onClick={copy} title={copied ? "Copied" : "Copy"}>
      {copied ? <IcCheck /> : <IcCopy />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const PAGE_CSS = `
  .cc-page.is-create {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: ${T.bg};
  }
  .cc-hero {
    background:
      radial-gradient(ellipse 60% 80% at 0% 0%, rgba(61,145,228,0.12), transparent 55%),
      linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border-bottom: 1px solid rgba(15,23,42,0.06);
    padding: 22px 28px 18px;
  }
  .cc-hero-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }
  .cc-kicker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${T.themePrimary};
    margin-bottom: 6px;
  }
  .cc-hero h1 {
    margin: 0 0 4px;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .cc-hero p {
    margin: 0;
    font-size: 13px;
    color: ${T.textSecondary};
    line-height: 1.45;
  }
  .cc-hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .cc-cluster-picker {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 2px;
  }
  .cc-cluster-chip {
    flex: 0 0 auto;
    display: grid;
    grid-template-columns: 36px 1fr auto;
    gap: 10px;
    align-items: center;
    min-width: 240px;
    max-width: 300px;
    border: 1px solid rgba(15,23,42,0.08);
    background: #fff;
    border-radius: 14px;
    padding: 10px 12px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  }
  .cc-cluster-chip:hover { border-color: rgba(61,145,228,0.35); }
  .cc-cluster-chip.is-active {
    border-color: rgba(61,145,228,0.45);
    background: rgba(61,145,228,0.06);
    box-shadow: inset 0 0 0 1px rgba(61,145,228,0.1);
  }
  .cc-cluster-avatar {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #f1f5f9;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .cc-cluster-avatar img { width: 22px; height: 22px; object-fit: contain; }
  .cc-cluster-name {
    font-size: 13px;
    font-weight: 700;
    color: ${T.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cc-cluster-meta { font-size: 11px; color: ${T.textSecondary}; margin-top: 2px; }
  .cc-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 650;
    white-space: nowrap;
  }
  .cc-status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .cc-body {
    padding: 20px 28px 40px;
    width: 100%;
  }
  .cc-metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }
  .cc-metric {
    background: ${T.surface};
    border: 1px solid rgba(15,23,42,0.07);
    border-radius: 14px;
    padding: 14px 16px;
  }
  .cc-metric-label {
    font-size: 11px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${T.textMuted};
    margin-bottom: 8px;
  }
  .cc-metric-value {
    font-size: 15px;
    font-weight: 700;
    color: ${T.textPrimary};
    line-height: 1.3;
    word-break: break-word;
  }
  .cc-card {
    background: ${T.surface};
    border: 1px solid rgba(15,23,42,0.07);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(15,23,42,0.03);
  }
  .cc-card-head {
    padding: 16px 20px;
    border-bottom: 1px solid rgba(15,23,42,0.06);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    background: linear-gradient(180deg, #fff, #fafbfc);
  }
  .cc-card-head-left {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .cc-card-icon {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: color-mix(in srgb, ${T.themePrimary} 12%, #ffffff);
    color: ${T.themePrimary};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .cc-card-title { font-size: 14px; font-weight: 600; color: ${T.textPrimary}; }
  .cc-card-sub { font-size: 12px; color: ${T.textMuted}; margin-top: 2px; line-height: 1.4; }
  .cc-lock-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border-radius: 999px;
    background: #f1f5f9;
    color: ${T.textSecondary};
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .cc-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    padding: 18px 20px;
  }
  .cc-value-tile {
    border: 1px solid rgba(15,23,42,0.08);
    background: #f8fafc;
    border-radius: 12px;
    padding: 14px 16px;
    min-width: 0;
  }
  .cc-value-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }
  .cc-value-label {
    font-size: 11px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${T.textMuted};
  }
  .cc-value-text {
    font-family: ${T.fontMono};
    font-size: 13px;
    color: ${T.textPrimary};
    word-break: break-all;
    line-height: 1.45;
  }
  .cc-value-text.is-empty { color: ${T.textMuted}; font-family: ${T.fontSans}; font-size: 13px; }
  .cc-copy {
    border: 0.5px solid ${T.borderMid};
    background: #fff;
    color: ${T.textSecondary};
    border-radius: 7px;
    height: 26px;
    padding: 0 8px;
    font-size: 11px;
    font-weight: 600;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
  }
  .cc-copy:hover { border-color: rgba(61,145,228,0.4); color: ${T.themePrimary}; }
  .cc-btn {
    border: 1px solid rgba(15,23,42,0.1);
    background: #fff;
    color: #334155;
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 12.5px;
    font-weight: 600;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
  }
  .cc-btn:hover { border-color: rgba(61,145,228,0.35); color: var(--active-color, #033b70); }
  .cc-btn.primary {
    background: ${T.themePrimary};
    border-color: transparent;
    color: #fff;
  }
  .cc-btn.primary:hover { filter: brightness(0.96); color: #fff; }
  .cc-create-bar {
    background: ${T.surface};
    border-bottom: 0.5px solid ${T.border};
    padding: 0 28px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  .cc-create-bar-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .cc-create-bar-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${T.themePrimary};
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  }
  .cc-create-bar-title { font-size: 14px; font-weight: 600; color: ${T.textPrimary}; line-height: 1.2; }
  .cc-create-bar-sub { font-size: 11px; color: ${T.textMuted}; margin-top: 1px; }
  .cc-wizard {
    flex: 1;
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    min-height: calc(100vh - 60px);
  }
  .cc-wizard-aside {
    background: ${T.surface};
    border-right: 0.5px solid ${T.border};
    padding: 24px 16px;
  }
  .cc-wizard-aside-label {
    font-size: 11px;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${T.textMuted};
    margin: 0 8px 12px;
  }
  .cc-wizard-main {
    padding: 24px 28px 48px;
    min-width: 0;
  }
  .cc-stepper { display: flex; flex-direction: column; }
  .cc-step {
    display: grid;
    grid-template-columns: 36px 1fr;
    gap: 12px;
    padding: 4px 8px;
    border-radius: 12px;
  }
  .cc-step.is-active { background: #eff6ff; }
  .cc-step-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .cc-step-num {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e2e8f0;
    color: ${T.textMuted};
    font-size: 13px;
    font-weight: 650;
    flex-shrink: 0;
  }
  .cc-step.is-active .cc-step-num {
    background: ${T.themePrimary};
    color: #fff;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.16);
  }
  .cc-step.is-done .cc-step-num { background: #dcfce7; color: #15803d; }
  .cc-step-line {
    width: 2px;
    flex: 1;
    min-height: 20px;
    margin: 6px 0 2px;
    border-radius: 999px;
    background: #e2e8f0;
  }
  .cc-step.is-done .cc-step-line { background: ${T.themePrimary}; }
  .cc-step-copy { padding: 8px 4px 18px 0; }
  .cc-step-label { font-size: 13px; font-weight: 500; color: ${T.textSecondary}; }
  .cc-step.is-active .cc-step-label { font-weight: 650; color: ${T.textPrimary}; }
  .cc-step-hint { font-size: 11px; color: ${T.textMuted}; margin-top: 2px; line-height: 1.35; }
  .cc-info-box {
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #eff6ff;
    border: 1px solid rgba(59,130,246,0.18);
    color: #1e3a8a;
    font-size: 12.5px;
    line-height: 1.45;
  }
  .cc-review-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }
  .cc-review-tile {
    background: #fff;
    border: 0.5px solid ${T.border};
    border-radius: 10px;
    padding: 14px;
    min-height: 88px;
  }
  .cc-review-label {
    font-size: 10px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${T.textMuted};
    margin-bottom: 8px;
  }
  .cc-review-value {
    font-family: ${T.fontMono};
    font-size: 13px;
    color: ${T.textPrimary};
    word-break: break-all;
    line-height: 1.45;
  }
  .cc-review-value.is-empty {
    font-family: ${T.fontSans};
    color: ${T.textMuted};
  }
  .cc-card-body { padding: 20px; }
  .cc-field { margin-bottom: 16px; }
  .cc-field:last-child { margin-bottom: 0; }
  .cc-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${T.textSecondary};
    margin-bottom: 6px;
  }
  .cc-required { color: #dc2626; margin-left: 3px; }
  .cc-hint { font-size: 11px; color: ${T.textMuted}; margin-top: 5px; line-height: 1.4; }
  .cc-error { font-size: 11px; color: #dc2626; margin-top: 5px; }
  .cc-input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 0.5px solid ${T.borderMid};
    border-radius: 8px;
    background: #f8fafc;
    font-family: ${T.fontSans};
    font-size: 13px;
    color: ${T.textPrimary};
    outline: none;
    box-sizing: border-box;
  }
  .cc-input.is-invalid { border-color: #dc2626; }
  .cc-input.is-mono { font-family: ${T.fontMono}; font-size: 12px; }
  .cc-input:focus {
    border-color: ${T.themePrimary};
    box-shadow: 0 0 0 3px rgba(61,145,228,0.12);
    background: #fff;
  }
  .cc-form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
  .cc-actions-bar {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 0.5px solid ${T.border};
    background: #fff;
  }
  .cc-empty {
    text-align: center;
    padding: 48px 20px;
    background: ${T.surface};
    border: 1px solid rgba(15,23,42,0.07);
    border-radius: 16px;
    color: ${T.textSecondary};
  }
  .cc-empty-title { font-size: 15px; font-weight: 650; color: ${T.textPrimary}; margin-bottom: 6px; }
  .cc-empty-sub { font-size: 13px; color: ${T.textMuted}; margin-bottom: 16px; }
  .cc-skel {
    height: 10px;
    border-radius: 6px;
    background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%);
    background-size: 200% 100%;
    animation: ccSkel 1.2s ease infinite;
  }
  @keyframes ccSkel { to { background-position: -200% 0; } }
  @media (max-width: 900px) {
    .cc-hero, .cc-body, .cc-create-bar, .cc-wizard-main { padding-left: 16px; padding-right: 16px; }
    .cc-metrics, .cc-grid, .cc-form-grid, .cc-review-grid { grid-template-columns: 1fr 1fr; }
    .cc-wizard { grid-template-columns: 1fr; }
    .cc-wizard-aside { display: none; }
  }
  @media (max-width: 640px) {
    .cc-metrics, .cc-grid, .cc-form-grid { grid-template-columns: 1fr; }
  }
`;

const ClusterConfig = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clusters, setClusters] = useState([]);
  const [loadingClusters, setLoadingClusters] = useState(true);
  const [selectedClusterId, setSelectedClusterId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [createClusterId, setCreateClusterId] = useState("");
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const id = "sa-cluster-config-styles";
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement("style");
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = PAGE_CSS;
  }, []);

  const selectedCluster = useMemo(
    () => clusters.find((c) => String(getClusterId(c)) === String(selectedClusterId)),
    [clusters, selectedClusterId]
  );

  const clusterOptions = useMemo(
    () => clusters.map((c) => ({
      value: String(getClusterId(c)),
      label: c.name || c.clusterName || getClusterId(c),
    })),
    [clusters]
  );

  const loadClusters = useCallback(() => {
    setLoadingClusters(true);
    return ClusterService.getClusterList(0, CLUSTER_PAGE_SIZE)
      .then((res) => {
        if (res?.status === API_RESPONSE.SUCCESS) {
          const list = res?.k8sClusterDTOPage?.content || [];
          setClusters(list);
          setSelectedClusterId((prev) => {
            if (prev && list.some((c) => String(getClusterId(c)) === String(prev))) return prev;
            return String(getClusterId(list[0]) || "");
          });
        } else {
          setClusters([]);
        }
      })
      .catch(() => setClusters([]))
      .finally(() => setLoadingClusters(false));
  }, []);

  useEffect(() => {
    loadClusters();
  }, [loadClusters]);

  useEffect(() => {
    if (!selectedClusterId) {
      setForm(EMPTY_FORM);
      setLoadingConfig(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingConfig(true);
    setForm(EMPTY_FORM);
    ClusterService.getClusterConfig(selectedClusterId)
      .then((res) => {
        if (cancelled) return;
        if (res?.status === API_RESPONSE.SUCCESS) {
          const cfg = extractConfig(res, selectedClusterId);
          setForm({
            domain: cfg?.domain || "",
            certs: cfg?.certs || "",
          });
        } else {
          setForm(EMPTY_FORM);
        }
      })
      .catch(() => {
        if (!cancelled) setForm(EMPTY_FORM);
      })
      .finally(() => {
        if (!cancelled) setLoadingConfig(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedClusterId]);

  const goToClusters = () => {
    const next = new URLSearchParams(searchParams);
    next.set("view", "cluster");
    setSearchParams(next, { replace: false });
  };

  const openCreate = () => {
    setShowCreate(true);
    setCreateStep(1);
    setSubmitted(false);
    setCreateForm(EMPTY_FORM);
    setCreateClusterId(selectedClusterId || String(getClusterId(clusters[0]) || ""));
  };

  const closeCreate = () => {
    setShowCreate(false);
    setCreateStep(1);
    setSubmitted(false);
    setSaving(false);
    setCreateForm(EMPTY_FORM);
  };

  const createErrors = useMemo(() => {
    const next = {};
    if (!createClusterId) next.clusterId = clusterMessages.selectCluster;
    const domain = createForm.domain.trim();
    if (!domain) next.domain = clusterMessages.domainEmpty;
    else if (hasHTMLTags(domain) || !isValidDomain(domain)) next.domain = clusterMessages.domainInvalid;
    const cert = createForm.certs.trim();
    if (!cert) next.certs = clusterMessages.certEmpty;
    else if (hasHTMLTags(cert) || !isValidCertArn(cert)) next.certs = clusterMessages.certInvalid;
    return next;
  }, [createClusterId, createForm]);

  const goCreateNext = () => {
    setSubmitted(true);
    if (!createClusterId) return;
    setSubmitted(false);
    setCreateStep(2);
  };

  const handleCreate = async () => {
    setSubmitted(true);
    if (createErrors.clusterId || createErrors.domain || createErrors.certs) {
      if (createErrors.clusterId) setCreateStep(1);
      return;
    }
    setSaving(true);
    const payload = {
      clusterId: createClusterId,
      domain: createForm.domain.trim(),
      certs: createForm.certs.trim(),
    };
    try {
      const res = await ClusterService.saveClusterConfig(payload);
      if (res?.status === API_RESPONSE.SUCCESS) {
        const saved = extractConfig(res, createClusterId) || payload;
        const next = {
          domain: saved.domain || payload.domain,
          certs: saved.certs || payload.certs,
        };
        setCreateForm(next);
        setSelectedClusterId(createClusterId);
        setForm(next);
        showNotificationGlobal(clusterMessages.configSaved, "success", "bottomRight");
        closeCreate();
      } else {
        showNotificationGlobal(res?.errorMessage || clusterMessages.configSaveFailed, "error", "bottomRight");
      }
    } catch (err) {
      showNotificationGlobal(err?.message || clusterMessages.configSaveFailed, "error", "bottomRight");
    } finally {
      setSaving(false);
    }
  };

  const status = getClusterStatusStyle(selectedCluster?.clusterStatus);
  const clusterType = convertTextToTitleCase(
    selectedCluster?.provisionerType || selectedCluster?.cloudType || "Kubernetes"
  );

  if (showCreate) {
    const createCluster = clusters.find((c) => String(getClusterId(c)) === String(createClusterId));
    const createStatus = getClusterStatusStyle(createCluster?.clusterStatus);

    return (
      <div className="cc-page is-create">
        <div className="cc-create-bar">
          <div className="cc-create-bar-left">
            <div className="cc-create-bar-icon"><IcGlobe /></div>
            <div>
              <div className="cc-create-bar-title">Create workload</div>
              <div className="cc-create-bar-sub">
                Step {createStep} of 2 · {CREATE_STEPS[createStep - 1]?.label}
              </div>
            </div>
          </div>
          <button type="button" className="cc-btn" onClick={closeCreate} disabled={saving}>
            <IcArrowLeft /> Back
          </button>
        </div>

        <div className="cc-wizard">
          <aside className="cc-wizard-aside">
            <div className="cc-wizard-aside-label">Steps</div>
            <CreateStepper current={createStep} />
          </aside>

          <div className="cc-wizard-main">
            {createStep === 1 ? (
              <div className="cc-card">
                <div className="cc-card-head">
                  <div className="cc-card-head-left">
                    <span className="cc-card-icon"><IcGlobe /></span>
                    <div>
                      <div className="cc-card-title">Select cluster</div>
                      <div className="cc-card-sub">The workload will be created on the selected cluster</div>
                    </div>
                  </div>
                </div>
                <div className="cc-card-body">
                  <Field
                    label="Cluster"
                    required
                    error={submitted && !createClusterId ? clusterMessages.selectCluster : ""}
                    hint="Pick the cluster first. You will enter domain and certificate next."
                  >
                    <SearchSelect
                      value={createClusterId}
                      onChange={(value) => {
                        setCreateClusterId(value);
                        setSubmitted(false);
                      }}
                      options={clusterOptions}
                      placeholder="Select a cluster"
                      searchPlaceholder="Search clusters…"
                      emptyMessage="No clusters found"
                      height={40}
                      hasError={submitted && !createClusterId}
                    />
                  </Field>
                  {createCluster ? (
                    <div className="cc-info-box" style={{ marginTop: 4 }}>
                      Selected <strong>{createCluster.name || createCluster.clusterName || createClusterId}</strong>
                      {" · "}
                      {[
                        convertTextToTitleCase(createCluster.provisionerType || createCluster.cloudType || "Kubernetes"),
                        createCluster.region,
                      ].filter(Boolean).join(" · ")}
                      {" · "}
                      {createStatus.label}
                    </div>
                  ) : null}
                </div>
                <div className="cc-actions-bar">
                  <button type="button" className="cc-btn primary" onClick={goCreateNext}>
                    Next step →
                  </button>
                </div>
              </div>
            ) : (
              <div className="cc-card">
                <div className="cc-card-head">
                  <div className="cc-card-head-left">
                    <span className="cc-card-icon"><IcGlobe /></span>
                    <div>
                      <div className="cc-card-title">Domain & TLS</div>
                      <div className="cc-card-sub">
                        Enter the domain name and certificate for {createCluster?.name || createCluster?.clusterName || "this cluster"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="cc-card-body">
                  <div className="cc-form-grid">
                    <Field
                      label="Domain name"
                      required
                      error={submitted ? createErrors.domain : ""}
                      hint="Wildcard domains are supported, for example *.app-dev-3.test.ai"
                    >
                      <Input
                        type="text"
                        value={createForm.domain}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, domain: e.target.value }))}
                        placeholder="*.app-dev-3.test.ai"
                        autoComplete="off"
                        error={submitted && Boolean(createErrors.domain)}
                      />
                    </Field>
                    <Field
                      label="Certificate"
                      required
                      error={submitted ? createErrors.certs : ""}
                      hint="Enter an ACM certificate ARN"
                    >
                      <Input
                        type="text"
                        value={createForm.certs}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, certs: e.target.value }))}
                        placeholder="arn:aws:acm:ap-south-1:123456789012:certificate/…"
                        autoComplete="off"
                        error={submitted && Boolean(createErrors.certs)}
                        mono
                      />
                    </Field>
                  </div>
                </div>
                <div className="cc-actions-bar">
                  <button type="button" className="cc-btn" onClick={() => setCreateStep(1)} disabled={saving}>
                    ← Back
                  </button>
                  <button
                    type="button"
                    className="cc-btn primary"
                    onClick={handleCreate}
                    disabled={saving || !createClusterId}
                  >
                    {saving ? "Creating…" : <><IcPlus /> Create workload</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-page">
      <div className="cc-hero">
        <div className="cc-hero-top">
          <div>
            <div className="cc-kicker">
              <IcGlobe />
              Cluster · Configuration
            </div>
            <h1>Cluster configuration</h1>
            <p>Review the domain and TLS certificate assigned to a cluster.</p>
          </div>
          <div className="cc-hero-actions">
            <button type="button" className="cc-btn" onClick={goToClusters}>
              Manage clusters
            </button>
            <button
              type="button"
              className="cc-btn primary"
              onClick={openCreate}
              disabled={loadingClusters || clusters.length === 0}
            >
              <IcPlus /> Create workload
            </button>
          </div>
        </div>

        <div className="cc-cluster-picker" role="listbox" aria-label="Clusters">
          {loadingClusters && clusters.length === 0 ? (
            [0, 1, 2].map((i) => (
              <div key={i} className="cc-cluster-chip" style={{ pointerEvents: "none" }}>
                <div className="cc-cluster-avatar">
                  <div className="cc-skel" style={{ width: 22, height: 22, borderRadius: 6 }} />
                </div>
                <div style={{ width: "100%" }}>
                  <div className="cc-skel" style={{ width: "70%", marginBottom: 6 }} />
                  <div className="cc-skel" style={{ width: "45%" }} />
                </div>
              </div>
            ))
          ) : clusters.length === 0 ? (
            <div className="cc-cluster-chip" style={{ cursor: "default", opacity: 0.85 }}>
              <div className="cc-cluster-avatar"><IcGlobe /></div>
              <div>
                <div className="cc-cluster-name">No clusters yet</div>
                <div className="cc-cluster-meta">Create one to add a workload</div>
              </div>
            </div>
          ) : (
            clusters.map((c) => {
              const id = getClusterId(c);
              const active = String(id) === String(selectedClusterId);
              const st = getClusterStatusStyle(c.clusterStatus);
              const logo = provisionerTypeLogo(c.provisionerType || c.cloudType);
              return (
                <button
                  key={id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`cc-cluster-chip${active ? " is-active" : ""}`}
                  onClick={() => setSelectedClusterId(String(id))}
                >
                  <span className="cc-cluster-avatar">
                    {logo ? <img src={logo} alt="" /> : <IcGlobe />}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <div className="cc-cluster-name">{c.name || c.clusterName || id}</div>
                    <div className="cc-cluster-meta">
                      {[convertTextToTitleCase(c.provisionerType || c.cloudType || "Kubernetes"), c.region]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </span>
                  <span className="cc-status" style={{ background: st.bg, color: st.color }}>
                    <span className="cc-status-dot" />
                    {st.label}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="cc-body">
        {loadingClusters && clusters.length === 0 ? (
          <>
            <div className="cc-metrics">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="cc-metric">
                  <div className="cc-skel" style={{ width: 56, marginBottom: 10 }} />
                  <div className="cc-skel" style={{ width: 96, height: 16 }} />
                </div>
              ))}
            </div>
            <div className="cc-card">
              <div className="cc-card-head">
                <div className="cc-card-head-left">
                  <span className="cc-card-icon"><IcGlobe /></span>
                  <div>
                    <div className="cc-skel" style={{ width: 120, marginBottom: 8 }} />
                    <div className="cc-skel" style={{ width: 220 }} />
                  </div>
                </div>
              </div>
              <div className="cc-grid">
                <div className="cc-value-tile">
                  <div className="cc-skel" style={{ width: 80, marginBottom: 10 }} />
                  <div className="cc-skel" style={{ width: "70%", height: 16 }} />
                </div>
                <div className="cc-value-tile">
                  <div className="cc-skel" style={{ width: 80, marginBottom: 10 }} />
                  <div className="cc-skel" style={{ width: "85%", height: 16 }} />
                </div>
              </div>
            </div>
          </>
        ) : !loadingClusters && clusters.length === 0 ? (
          <div className="cc-empty">
            <div className="cc-empty-title">No clusters yet</div>
            <div className="cc-empty-sub">Create a cluster first, then come back to add a workload.</div>
            <button type="button" className="cc-btn primary" onClick={goToClusters}>
              Manage clusters
            </button>
          </div>
        ) : (
          <>
            <div className="cc-metrics">
              <div className="cc-metric">
                <div className="cc-metric-label">Status</div>
                {selectedCluster ? (
                  <span className="cc-status" style={{ background: status.bg, color: status.color }}>
                    <span className="cc-status-dot" />
                    {status.label}
                  </span>
                ) : (
                  <div className="cc-skel" style={{ width: 72, height: 18 }} />
                )}
              </div>
              <div className="cc-metric">
                <div className="cc-metric-label">Provider</div>
                <div className="cc-metric-value">{selectedCluster ? clusterType : "—"}</div>
              </div>
              <div className="cc-metric">
                <div className="cc-metric-label">Region</div>
                <div className="cc-metric-value">{selectedCluster?.region || "—"}</div>
              </div>
              <div className="cc-metric">
                <div className="cc-metric-label">Zone</div>
                <div className="cc-metric-value">{selectedCluster?.zone || "—"}</div>
              </div>
            </div>

            <div className="cc-card">
              <div className="cc-card-head">
                <div className="cc-card-head-left">
                  <span className="cc-card-icon"><IcGlobe /></span>
                  <div>
                    <div className="cc-card-title">Ingress & TLS</div>
                    <div className="cc-card-sub">Domain name and ACM certificate used for HTTPS traffic</div>
                  </div>
                </div>
                <span className="cc-lock-badge"><IcLock /> Read only</span>
              </div>
              <div className="cc-grid">
                    <div className="cc-value-tile">
                      <div className="cc-value-top">
                        <span className="cc-value-label">Domain name</span>
                        <CopyButton value={form.domain.trim()} />
                      </div>
                      {loadingConfig ? (
                        <div className="cc-skel" style={{ width: "70%", height: 16 }} />
                      ) : form.domain.trim() ? (
                        <div className="cc-value-text">{form.domain.trim()}</div>
                      ) : (
                        <div className="cc-value-text is-empty">Not assigned</div>
                      )}
                    </div>
                    <div className="cc-value-tile">
                      <div className="cc-value-top">
                        <span className="cc-value-label">Certificate</span>
                        <CopyButton value={form.certs.trim()} />
                      </div>
                      {loadingConfig ? (
                        <div className="cc-skel" style={{ width: "85%", height: 16 }} />
                      ) : form.certs.trim() ? (
                        <div className="cc-value-text">{form.certs.trim()}</div>
                      ) : (
                        <div className="cc-value-text is-empty">Not assigned</div>
                      )}
                    </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClusterConfig;
